import React, { memo, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { CheckCircleIcon, XIcon } from '@phosphor-icons/react';
import { CURRENCY_CODE_VALUES } from '@erp/shared-types';

/**
 * IMPORTANT: Currency handling is currently a temporary solution.
 * This component is part of an older workflow but still uses the shared
 * currency codes.
 *
 * TODO: Future refactoring needed for proper multi-currency support.
 * @see packages/shared-types/src/currency.ts for more details
 */

interface DataValidationAndConstantsProps {
  sumCheck: number | null;
  transformedData?: unknown[][]; // For calculating transformed total
  constants: Record<string, unknown>;
  onConstantsChange: (constants: Record<string, unknown>) => void;
  disabled?: boolean;
}

// Currency options for dropdown
// NOTE: Uses shared currency codes from @erp/shared-types
const CURRENCY_OPTIONS = CURRENCY_CODE_VALUES.map((code) => ({
  code,
  label: `${code} - ${getCurrencyLabel(code)}`,
}));

function getCurrencyLabel(code: string): string {
  const labels: Record<string, string> = {
    CAD: 'Canadian Dollar',
    USD: 'US Dollar',
    EUR: 'Euro',
    GBP: 'British Pound',
  };
  return labels[code] || code;
}

const DataValidationAndConstants: React.FC<DataValidationAndConstantsProps> =
  memo(
    ({
      sumCheck,
      transformedData,
      constants,
      onConstantsChange,
      disabled = false,
    }) => {
      const handleChange = useCallback(
        (key: string, value: string) => {
          onConstantsChange({
            ...constants,
            [key]: value,
          });
        },
        [constants, onConstantsChange],
      );

      // Calculate transformed total from 'Value' column
      const calculateTransformedTotal = () => {
        if (!transformedData || transformedData.length === 0) {
          return null;
        }

        // Find the 'Value' column index (case-insensitive)
        const headerRow = transformedData[0];
        if (!headerRow) return null;

        const valueColumnIndex = headerRow.findIndex(
          (header) =>
            typeof header === 'string' &&
            header.toLowerCase().trim() === 'value',
        );

        if (valueColumnIndex === -1) return null;

        // Sum up all values in the Value column (excluding header)
        let total = 0;
        for (let i = 1; i < transformedData.length; i++) {
          const row = transformedData[i];
          if (row && row[valueColumnIndex] !== undefined) {
            const value = parseFloat(
              String(row[valueColumnIndex]).replace(/[,$]/g, ''),
            );
            if (!isNaN(value)) {
              total += value;
            }
          }
        }

        return total;
      };

      const transformedTotal = calculateTransformedTotal();

      // Format currency values
      const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(value);
      };

      // Determine if validation passed
      const validationPassed =
        sumCheck !== null &&
        transformedTotal !== null &&
        Math.abs(sumCheck - transformedTotal) < 0.01; // Allow for small rounding differences

      // Don't render if no data validation and no constants
      if (
        sumCheck === null &&
        (!constants || Object.keys(constants).length === 0)
      ) {
        return null;
      }

      return (
        <Card className="mb-2">
          <CardContent className="p-6">
            <div className="flex gap-4 items-start">
              {/* Data Validation Section */}
              {sumCheck !== null && (
                <div className="flex-1">
                  <div className="flex items-center gap-1 mb-1">
                    {validationPassed ? (
                      <CheckCircleIcon className="w-4 h-4 text-green-600" />
                    ) : (
                      <XIcon className="w-4 h-4 text-red-600" />
                    )}
                    <h3 className="text-sm font-medium">Data Validation</h3>
                  </div>

                  <p className="text-sm text-gray-600">
                    {transformedTotal !== null ? (
                      validationPassed ? (
                        <>
                          Transformed data adds up correctly:{' '}
                          {formatCurrency(sumCheck)} ={' '}
                          {formatCurrency(transformedTotal)}
                        </>
                      ) : (
                        <>
                          Values don&apos;t match: {formatCurrency(sumCheck)} ≠{' '}
                          {formatCurrency(transformedTotal)}
                        </>
                      )
                    ) : (
                      <>AI-calculated total: {formatCurrency(sumCheck)}</>
                    )}
                  </p>
                </div>
              )}

              {/* Vertical Divider if both sections exist */}
              {sumCheck !== null &&
                constants &&
                Object.keys(constants).length > 0 && (
                  <Separator orientation="vertical" className="mx-2 h-auto" />
                )}

              {/* Constants Section */}
              {constants && Object.keys(constants).length > 0 && (
                <div className="flex-[2]">
                  <h3 className="text-sm font-medium mb-2">
                    Extracted Information
                  </h3>

                  <div className="flex flex-row gap-2 flex-wrap">
                    {/* Supplier Name */}
                    {!!constants.supplierName && (
                      <div className="min-w-[200px]">
                        <Label
                          htmlFor="supplier-name"
                          className="text-sm font-medium"
                        >
                          Supplier Name
                        </Label>
                        <Input
                          id="supplier-name"
                          value={constants.supplierName as string}
                          onChange={(e) =>
                            handleChange('supplierName', e.target.value)
                          }
                          disabled={disabled}
                          className="mt-1"
                        />
                      </div>
                    )}

                    {/* Currency */}
                    {!!constants.currency && (
                      <div className="min-w-[180px]">
                        <Label
                          htmlFor="currency"
                          className="text-sm font-medium"
                        >
                          Currency
                        </Label>
                        <select
                          id="currency"
                          value={constants.currency as string}
                          onChange={(e) =>
                            handleChange('currency', e.target.value)
                          }
                          disabled={disabled}
                          className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        >
                          {CURRENCY_OPTIONS.map((option) => (
                            <option key={option.code} value={option.code}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Other dynamic constants */}
                    {Object.entries(constants)
                      .filter(
                        ([key]) => !['supplierName', 'currency'].includes(key),
                      )
                      .map(([key, value]) => (
                        <div key={key} className="min-w-[150px]">
                          <Label htmlFor={key} className="text-sm font-medium">
                            {key.charAt(0).toUpperCase() + key.slice(1)}
                          </Label>
                          <Input
                            id={key}
                            value={value as string}
                            onChange={(e) => handleChange(key, e.target.value)}
                            disabled={disabled}
                            className="mt-1"
                          />
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      );
    },
  );

DataValidationAndConstants.displayName = 'DataValidationAndConstants';

export default DataValidationAndConstants;
